<template>
  <div class="container has-text-left">
    <div class="block">
      <b-field label="Name">
        <b-input required v-model="name" placeholder="Enter user friendly name"/>
      </b-field>
    </div>
    <div class="block">
      <hr/>
      <div class="buttons" v-if="visibleButtons">
        <b-button type="is-primary" class="is-small" @click="handleToggleAddEvent">Add Event</b-button>
        <b-button type="is-primary" class="is-small" @click="handleToggleAddSchedule">Add Schedule</b-button>
        <b-button type="is-primary" class="is-small" @click="handleToggleAddCondition">Add Condition</b-button>
        <b-button type="is-primary" class="is-small" @click="handleToggleAddAction">Add Action</b-button>
      </div>
      <div class="container has-text-left" v-if="event.visible">
        <div class="level">
          <div class="level-left">
            <div class="level-item">Object:</div>
            <div class="level-item">
              <b-select v-model="event.objectTypeId">
                <option v-for="objectType in optionsForEventObjectType"
                        :key="objectType.id"
                        :value="objectType.id"
                >{{objectType.name}}
                </option>
              </b-select>
            </div>
            <div class="level-item" v-if="event.objectTypeId > 0">
              <b-select v-model="event.objectId">
                <option
                    v-for="object in availableEventObjects"
                    :key="object.id"
                    :value="object.id"
                >{{object.name}}
                </option>
              </b-select>
            </div>
          </div>
        </div>
        <div class="level" v-if="event.objectId > 0">
          <div class="level-left">
            <div class="level-item">Operator:</div>
            <div class="level-item">
              <b-select v-model="event.evaluation">
                <option v-for="evaluation in eventEvaluationTemplates"
                        :key="evaluation.id"
                        :value="evaluation.id"
                >{{evaluation.name}}
                </option>
              </b-select>
            </div>
          </div>
        </div>
        <div class="level"
             v-if="event.objectId > 0 && event.evaluation > 0 && selectedEvaluation && selectedEvaluation.args && selectedEvaluation.args.length > 0">
          <div class="level-left">
            <div class="level-item" v-for="(arg, index) in selectedEvaluation.args"
                 :key="arg.label"
            >
              <span style="margin-right: 0.5rem;">{{arg.label}}</span>
              <b-input required v-model="event.args[index]"/>
            </div>
          </div>
        </div>
        <div class="buttons">
          <b-button type="is-danger-outlined" class="is-small" @click="handleCancelAddEvent">Back</b-button>
          <b-button v-if="event.objectId > 0 && event.evaluation > 0"
                    type="is-success"
                    class="is-small"
                    @click="handleAddEvent"
          >Add Event
          </b-button>
        </div>
      </div>
      <div class="container has-text-left" v-if="schedule.visible">
        <div class="block">
          <b-field label="Repeat">
            <b-select v-model="schedule.repeatId">
              <option v-for="scheduleRepeat in scheduleRepeatTemplates"
                      :key="scheduleRepeat.id"
                      :value="scheduleRepeat.id"
              >{{scheduleRepeat.name}}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="block"
             v-if="selectedRepeatTemplate && selectedRepeatTemplate.args && selectedRepeatTemplate.args.length > 0">
          <b-field v-for="arg in selectedRepeatTemplate.args"
                   :key="arg.name"
                   :value="arg.name"
          >
            <b-input v-if="arg.type === 'number'"
                     v-model="schedule.repeatArgs[arg.name]"
                     :placeholder="arg.description"
            />
            <app-day-of-week-picker v-if="arg.type === 'dow'"
                                    v-model="schedule.repeatArgs[arg.name]"
            />
            <b-timepicker v-if="arg.type === 'time'"
                          :placeholder="arg.description"
                          icon="clock"
                          :enable-seconds="false"
                          v-model="schedule.repeatArgs[arg.name]"
            ></b-timepicker>
            <b-datepicker v-if="arg.type === 'date'"
                          :placeholder="arg.description"
                          :min-date="dateForYesterday"
                          v-model="schedule.repeatArgs[arg.name]"
            />
          </b-field>
        </div>
        <div class="buttons">
          <b-button type="is-danger-outlined" class="is-small" @click="handleCancelAddSchedule">Back</b-button>
          <b-button type="is-success"
                    class="is-small"
                    :disabled="!validScheduleRepeatArgs"
                    @click="handleAddSchedule"
          >Add Schedule
          </b-button>
        </div>
      </div>
      <div class="container has-text-left" v-if="condition.visible">
        <div class="level">
          <div class="level-left">
            <div class="level-item">Logic:</div>
            <div class="level-item">
              <b-select v-model="condition.logic">
                <option v-for="logic in logicOptions" :key="logic" :value="logic">{{logic}}</option>
              </b-select>
            </div>
          </div>
        </div>
        <div class="level">
          <div class="level-left">
            <div class="level-item">Object:</div>
            <div class="level-item">
              <b-select v-model="condition.current.objectTypeId">
                <option v-for="objectType in optionsForEventObjectType"
                        :key="objectType.id"
                        :value="objectType.id"
                >{{objectType.name}}
                </option>
              </b-select>
            </div>
            <div class="level-item" v-if="condition.current.objectTypeId > 0">
              <b-select v-model="condition.current.objectId">
                <option
                    v-for="object in availableConditionObjects"
                    :key="object.id"
                    :value="object.id"
                >{{object.name}}
                </option>
              </b-select>
            </div>
            <div class="level-item" v-if="condition.current.objectId > 0">
              <b-select v-model="condition.current.operator">
                <option
                    v-for="op in operatorsOptions"
                    :key="op"
                    :value="op"
                >{{op}}
                </option>
              </b-select>
            </div>
            <div class="level-item" v-if="condition.current.objectId > 0">
              <b-input placeholder="Enter value" v-model="condition.current.value"/>
            </div>
          </div>
        </div>
        <div class="buttons">
          <b-button type="is-danger-outlined" class="is-small" @click="handleCancelAddCondition">Back</b-button>
          <b-button v-if="condition.current.objectId > 0 && condition.current.value.length > 0"
                    type="is-success"
                    class="is-small"
                    @click="handleAddCondition"
          >Add Condition
          </b-button>
        </div>
      </div>
      <div class="container has-text-left" v-if="action.visible">
        <div class="level">
          <div class="level-left">
            <div class="level-item">Action type:</div>
            <div class="level-item">
              <b-select v-model="action.type">
                <option v-for="act in availableActionTypes" :key="act" :value="act">{{act}}</option>
              </b-select>
            </div>
          </div>
        </div>
        <div class="container" v-if="action.type === 'command'">
          <div class="block">
            <b-radio
                v-model="commandInputMode"
                name="commandInputMode"
                native-value="1"
            >Simple
            </b-radio>
            <b-radio
                v-model="commandInputMode"
                name="commandInputMode"
                native-value="2"
            >Advanced
            </b-radio>
          </div>
          <div class="container" v-if="commandInputMode === '1'">
            <div class="level">
              <div class="level-left">
                <div class="level-item">Endpoint:</div>
                <div class="level-item">
                  <b-select v-model="action.command.endpointId">
                    <option
                        v-for="endpoint in availableEndpoints"
                        :key="endpoint.id"
                        :value="endpoint.id"
                    >{{endpoint.title}}
                    </option>
                  </b-select>
                </div>
              </div>
            </div>
            <div class="level" v-if="action.command.endpointId > 0">
              <div class="level-left">
                <div class="level-item">Message:</div>
                <div class="level-item">
                  <b-input placeholder="Enter message..." v-model="action.command.message" required/>
                </div>
              </div>
            </div>
          </div>
          <div class="container" v-if="commandInputMode === '2'">
            <div class="level">
              <div class="level-left">
                <div class="level-item">Topic for command:</div>
                <div class="level-item">
                  <b-input placeholder="command/" v-model="action.command.command" required style="width: 20rem;"/>
                </div>
              </div>
            </div>
            <div class="level" v-if="action.command.command.length > 0">
              <div class="level-left">
                <div class="level-item">Message to send:</div>
                <div class="level-item">
                  <b-input placeholder="Enter message..." v-model="action.command.message" required/>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="container" v-if="action.type === 'set_object_state'">
          <div class="block">
            <b-select v-model="action.setObjectState.objectTypeId">
              <option v-for="objectType in optionsForEventObjectType"
                      :key="objectType.id"
                      :value="objectType.id"
              >{{objectType.name}}
              </option>
            </b-select>
          </div>
          <div class="block" v-if="action.setObjectState.objectTypeId > 0">
            <b-select v-model="action.setObjectState.objectId">
              <option :value="0">choose one...</option>
              <option v-for="object in objectsByObjectType(action.setObjectState.objectTypeId)"
                      :key="object.id"
                      :value="object.id"
              >{{object.name}}
              </option>
            </b-select>
          </div>
          <div class="block" v-if="action.setObjectState.objectId > 0">
            <b-input v-model="action.setObjectState.value" placeholder="Set value..." required/>
          </div>
        </div>
        <div class="container" v-if="action.type === 'run_automation'">
          <div class="block">
            <b-select v-model="action.runAutomation.id">
              <option :value="0">choose automation...</option>
              <option v-for="am in automationsAvailableForRun"
                      :key="am.id"
                      :value="am.id"
              >{{am.name}}
              </option>
            </b-select>
          </div>
        </div>
        <div class="container" v-if="action.type === 'notify'">
          <b-table
              :narrowed="true"
              :striped="true"
              :data="usersAvailableForNotifications"
          >
            <template slot-scope="props">
              <b-table-column field="username" label="Username">{{props.row.username}}</b-table-column>
              <b-table-column v-for="handler in notificationHandlers"
                              :key="handler.channel"
                              :value="handler.channel"
                              :label="handler.channel"
                              numeric
              >
                <b-checkbox v-model="action.notify.mapUserChannels[props.row.username][handler.channel]"
                            true-value="1"
                            false-value="0"
                            @input="handleNotificationCheckboxChanged"
                />
              </b-table-column>
            </template>
          </b-table>
        </div>
        <div class="container" v-if="action.type === 'code'">
          <div class="block">
            <b-input type="textarea" v-model="action.code" placeholder="Enter Javascript code...">{{action.code}}</b-input>
          </div>
        </div>
        <div class="buttons" style="margin-top: 1.25rem;">
          <b-button type="is-danger-outlined" class="is-small" @click="handleCancelAddAction">Back</b-button>
          <b-button v-if="isVisibleActionButton()"
                    type="is-success"
                    class="is-small"
                    @click="handleAddAction"
          >Add Action
          </b-button>
        </div>
      </div>
    </div>
    <div class="block">
      <hr/>
    </div>
    <div class="block" v-if="storedEvents.length > 0">
      <h6 class="title is-6">Events</h6>
      <div v-if="storedEvents.length > 0">
        <b-table
            :narrowed="true"
            :striped="true"
            :data="eventsTableData"
        >
          <template slot-scope="props">
            <b-table-column field="num" label="#" numeric>{{props.row.num}}</b-table-column>
            <b-table-column field="objectId" label="Object">{{props.row.object.name}}</b-table-column>
            <b-table-column field="evaluationName" label="Operation">{{props.row.evaluationName}}</b-table-column>
            <b-table-column field="remove" numeric>
              <b-button type="is-danger" class="is-small" @click="handleRemoveEvent(props.row.num)">Remove</b-button>
            </b-table-column>
          </template>
        </b-table>
      </div>
      <div v-else>None.</div>
    </div>
    <div class="block" v-if="storedSchedules.length > 0">
      <h6 class="title is-6">Schedules</h6>
      <div v-if="storedSchedules.length > 0">
        <ul>
          <li v-for="sch in storedSchedules"
              :key="sch.id"
          >
            <div class="level">
              <div class="level-left">
                <div class="level-item">
                  {{JSON.stringify(sch)}}
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <b-button type="is-danger" class="is-small" @click="handleRemoveSchedule(sch.id)">Remove
                  </b-button>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div v-else>None.</div>
    </div>
    <div class="block" v-if="storedCondition.list.length > 0">
      <h6 class="title is-6">Conditions</h6>
      <div v-if="storedCondition.list.length > 0">
        <div class="level">
          <div class="level-left">
            <div class="level-item">Logic:</div>
            <div class="level-item">{{storedCondition.logic}}</div>
          </div>
        </div>
        <b-table
            :narrowed="true"
            :striped="true"
            :data="conditionsTableData"
        >
          <template slot-scope="props">
            <b-table-column field="num" label="#" numeric>{{props.row.num}}</b-table-column>
            <b-table-column field="condition" label="Condition">{{props.row.description}}</b-table-column>
            <b-table-column field="remove" numeric>
              <b-button type="is-danger" class="is-small" @click="handleRemoveCondition(props.row.num)">Remove
              </b-button>
            </b-table-column>
          </template>
        </b-table>
      </div>
      <div v-else>None.</div>
    </div>
    <div class="block" v-if="storedActions.length > 0">
      <h6 class="title is-6">Actions</h6>
      <div v-if="storedActions.length > 0">
        <b-table
            :narrowed="true"
            :striped="true"
            :data="actionsTableData"
        >
          <template slot-scope="props">
            <b-table-column field="num" label="#" numeric>{{props.row.num}}</b-table-column>
            <b-table-column field="description" label="Action">{{props.row.description}}</b-table-column>
            <b-table-column field="remove" numeric>
              <b-button type="is-danger" class="is-small" @click="handleRemoveAction(props.row.num)">Remove</b-button>
            </b-table-column>
          </template>
        </b-table>
      </div>
      <div v-else>None.</div>
    </div>

    <div class="buttons">
      <b-button type="is-success" :disabled="!validCreateAutomation()" @click="handleCreateAutomation">Create</b-button>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import BField from "buefy/src/components/field/Field";
  import BInput from "buefy/src/components/input/Input";
  import BButton from "buefy/src/components/button/Button";
  import BSelect from "buefy/src/components/select/Select";
  import BTable from "buefy/src/components/table/Table";
  import BTableColumn from "buefy/src/components/table/TableColumn";
  import BRadio from "buefy/src/components/radio/Radio";
  import DayOfWeekPicker from "../configure/DayOfWeekPicker";
  import BTimepicker from "buefy/src/components/timepicker/Timepicker";
  import BCheckbox from "buefy/src/components/checkbox/Checkbox";
  import moment from 'moment';
  import BDatepicker from "buefy/src/components/datepicker/Datepicker";

  export default {
    components: {
      BDatepicker,
      BCheckbox,
      BTimepicker,
      BRadio,
      BTableColumn,
      BTable,
      BSelect,
      BButton,
      BInput,
      BField,
      appDayOfWeekPicker: DayOfWeekPicker
    },
    name: "create-automation",
    data() {
      return {
        name: '',
        visibleButtons: true,
        commandInputMode: "1",
        keyForAddAction: parseInt(moment().format('X')),
        buttonAddActionVisibleInActionNotify: false,
        event: {
          visible: false,
          objectTypeId: 0,
          objectId: 0,
          evaluation: 1,
          args: []
        },
        schedule: {
          visible: false,
          repeatId: 1,
          repeatArgs: {}
        },
        condition: {
          visible: false,
          logic: "and",
          current: {
            objectTypeId: 0,
            objectId: 0,
            operator: "==",
            value: ''
          }
        },
        action: {
          visible: false,
          type: "command",
          command: {
            command: '',
            message: '',
            endpointId: 0
          },
          setObjectState: {
            objectTypeId: 0,
            objectId: 0,
            value: ''
          },
          runAutomation: {
            id: 0
          },
          notify: {
            mapUserChannels: {}
          },
          code: ''
        },
        storedEvents: [],
        storedSchedules: [],
        storedCondition: {
          logic: "and",
          list: []
        },
        storedActions: [],
        currentScheduleId: 1,
        dateForYesterday: new Date()
      };
    },
    computed: {
      ...mapGetters('objects', ['groupById', 'objects', 'objectTypes', 'objectsByObjectType', 'objectById']),
      ...mapGetters('integrations', ['integrations']),
      ...mapGetters('notifications', ['notificationHandlers']),
      ...mapGetters('devices', ['devicesByIntegrationId', 'deviceEndpoints']),
      ...mapGetters('automations', ['eventEvaluationTemplates', 'conditionOperators', 'scheduleRepeatTemplates', 'scheduleRepeatTemplateById', 'automationsAvailableForRun', 'automationById']),
      ...mapGetters('auth', ['userList']),
      usersAvailableForNotifications() {
        const ret = [];

        this.userList.forEach(item => {
          ret.push({
            ...item
          });
        });

        return ret;
      },
      optionsForEventObjectType() {
        const ret = [
          {
            id: 0,
            name: 'Choose object type...'
          }
        ];

        this.objectTypes.forEach(item => {
          ret.push(item);
        });

        return ret;
      },
      availableEventObjects() {
        const ret = [
          {
            id: 0,
            name: 'Choose object...'
          }
        ];
        const list = this.objectsByObjectType(this.event.objectTypeId);
        list.forEach(item => {
          ret.push(item);
        });

        return ret;
      },
      availableConditionObjects() {
        const ret = [
          {
            id: 0,
            name: 'Choose object...'
          }
        ];
        const list = this.objectsByObjectType(this.condition.current.objectTypeId);
        list.forEach(item => {
          ret.push(item);
        });

        return ret;
      },
      selectedEvaluation() {
        const filtered = this.eventEvaluationTemplates.filter(item => {
          return parseInt(item.id) === parseInt(this.event.evaluation);
        });

        return typeof filtered[0] !== 'undefined' ? filtered[0] : null;
      },
      selectedRepeatTemplate() {
        return this.scheduleRepeatTemplateById(this.schedule.repeatId);
      },
      logicOptions() {
        return ["and", "or"];
      },
      operatorsOptions() {
        return this.conditionOperators;
      },
      availableActionTypes() {
        return [
          "command", "notify", "set_object_state", "run_automation", "code"
        ];
      },
      eventsTableData() {
        let num = 0;
        return this.storedEvents.map(item => {
          num++;
          const object = this.objectById(item.objectId);
          const e = this.eventEvaluationTemplates.filter(evalItem => {
            return parseInt(evalItem.id) === parseInt(item.evaluation);
          });
          const ev = e[0];
          let description = ev['name'];
          if (ev.args) {
            const arr = [];
            ev.args.forEach((arg, idx) => {
              arr.push(`${arg['label']} ${item.args[idx]}`);
            });
            description = arr.join(' ');
          }
          return {
            ...item,
            num,
            object,
            evaluationName: description
          };
        });
      },
      conditionsTableData() {
        let num = 0;
        return this.storedCondition.list.map(item => {
          num++;
          const object = this.objectById(item.objectId);
          const description = `${object.name} ${item.operator} ${item.value}`;
          return {
            ...item,
            num,
            description
          };
        });
      },
      actionsTableData() {
        let num = 0;
        return this.storedActions.map(item => {
          num++;
          let description = '';
          if (item['type'] === "") {
            description = `Execute ${item.command.command} -> ${item.command.message}`;
          }

          if (item['type'] === 'set_object_state') {
            const { objectId, value } = item.setObjectState;
            description = `Set object ${objectId} to ${value}`;
          }

          if (item['type'] === "run_automation") {
            const { id: automationId } = item.runAutomation;
            const targetAutomation = this.automationById(automationId);
            if (targetAutomation) {
              description = `Run automation "${targetAutomation.name}" [id: ${automationId}]`;
            }
          }

          if (item['type'] === "notify") {
            const { mapUserChannels } = item['notify'];
            const arrUsers = [];
            Object.keys(mapUserChannels).forEach(username => {
              const itemUser = mapUserChannels[username];
              let found = false;
              Object.keys(itemUser).forEach(channel => {
                if (parseInt(itemUser[channel]) === 1) {
                  found = true;
                }
              });
              if (found) {
                arrUsers.push(username);
              }
            });
            description = `Notify ${arrUsers.join(', ')}`;
          }

          if (item['type'] === "code") {
            description = `Execute code: ${item['code']}`;
          }

          return {
            ...item,
            num,
            description
          };
        });
      },
      availableEndpoints() {
        const ret = [
          {
            id: 0,
            title: 'Choose...'
          }
        ];

        this.deviceEndpoints.forEach(item => {
          ret.push({
            ...item,
            title: `${item.device_name} - ${item.name}`
          });
        });

        return ret;
      },
      validScheduleRepeatArgs() {
        let foundNotOk = false;
        if (parseInt(this.schedule.repeatId) === 4) {
          foundNotOk = typeof this.schedule.repeatArgs['days'] === "undefined" || this.schedule.repeatArgs['days'].length < 0;
        }

        return !foundNotOk;
      }
    },
    methods: {
      ...mapActions('automations', ['createAutomation']),
      isAnyUserNotified() {
        let foundChecked = false;
        Object.keys(this.action.notify.mapUserChannels).forEach(username => {
          const itemUsername = this.action.notify.mapUserChannels[username];
          Object.keys(itemUsername).forEach(channel => {
            const itemChannel = itemUsername[channel];
            if (parseInt(itemChannel) === 1) {
              foundChecked = true;
            }
          });
        });

        return foundChecked;
      },
      isVisibleActionButton() {
        if (this.action.type === "command" && this.action.command.message.length > 0) {
          return true;
        }

        if (this.action.type === "set_object_state" && this.action.setObjectState.objectId > 0 && this.action.setObjectState.value !== '') {
          return true;
        }

        if (this.action.type === "notify" && this.buttonAddActionVisibleInActionNotify) {
          return true;
        }

        if (this.action.type === "run_automation" && parseInt(this.action.runAutomation.id, 10) > 0) {
          return true;
        }

        if (this.action.type === "code" && this.action.code.length > 2) {
          return true;
        }

        return false;
      },
      handleNotificationCheckboxChanged() {
        this.buttonAddActionVisibleInActionNotify = this.isAnyUserNotified();
      },
      handleToggleAddEvent() {
        this.visibleButtons = false;
        this.event.visible = true;
      },
      handleToggleAddSchedule() {
        this.visibleButtons = false;
        this.schedule.visible = true;
      },
      handleToggleAddCondition() {
        this.visibleButtons = false;
        this.condition.visible = true;
      },
      handleToggleAddAction() {
        this.visibleButtons = false;
        this.action.visible = true;
      },
      handleAddSchedule() {
        const objToAdd = {
          id: this.currentScheduleId,
          repeat: {
            id: this.schedule.repeatId,
            args: { ...this.schedule.repeatArgs }
          }
        };
        const filtered = this.storedSchedules.filter(item => {
          return parseInt(item.id) === parseInt(objToAdd.repeat.id);
        });
        if (filtered.length < 1) {
          this.storedSchedules.push(objToAdd);
        }
        this.schedule.repeatId = 1;
        this.schedule.repeatArgs = {};
        this.currentScheduleId++;
        this.handleCancelAddSchedule();
      },
      handleAddEvent() {
        const objToAdd = { ...this.event };
        objToAdd['args'] = [...this.event.args];
        delete objToAdd['visible'];
        this.storedEvents.push(objToAdd);
        this.handleCancelAddEvent();
      },
      handleCancelAddEvent() {
        this.event.visible = false;
        this.visibleButtons = true;
      },
      handleCancelAddSchedule() {
        this.schedule.visible = false;
        this.visibleButtons = true;
      },
      handleAddCondition() {
        const objToAdd = { ...this.condition.current };
        this.storedCondition['list'].push(objToAdd);
        this.handleCancelAddCondition();
      },
      handleAddAction() {
        if (this.action.type === "notify") {
          this.storedActions = this.storedActions.filter(action => {
            return action.type !== "notify";
          });
        }
        const objToAdd = { ...this.action };
        delete objToAdd['visible'];
        this.storedActions.push(objToAdd);
        this.handleCancelAddAction();
      },
      handleCancelAddCondition() {
        this.condition.visible = false;
        this.visibleButtons = true;
      },
      handleCancelAddAction() {
        this.action.visible = false;
        this.visibleButtons = true;
      },
      handleRemoveEvent(num) {
        if (confirm("Are you sure ?")) {
          this.storedEvents = this.storedEvents.filter((item, index) => {
            return index + 1 !== parseInt(num);
          });
        }
      },
      handleRemoveSchedule(id) {
        this.storedSchedules = this.storedSchedules.filter(item => {
          return parseInt(item.id) !== parseInt(id);
        });
      },
      handleRemoveCondition(num) {
        if (confirm("Are you sure ?")) {
          this.storedCondition.list = this.storedCondition.list.filter((item, index) => {
            return index + 1 !== parseInt(num);
          });
        }
      },
      handleRemoveAction(num) {
        if (confirm("Are you sure ?")) {
          this.storedActions = this.storedActions.filter((item, index) => {
            return index + 1 !== parseInt(num);
          });
        }
      },
      validCreateAutomation() {
        const hasActions = this.storedActions.length > 0;

        return this.name.length > 0 && hasActions;
      },
      cleanActions(actions) {
        const act = [];
        actions.forEach(item => {
          const { command: commandObject, notify, runAutomation, setObjectState, type, code } = item;
          const itemToPush = { type: type };
          const { command, endpointId } = commandObject;
          if (command || endpointId) {
            itemToPush['command'] = { ...commandObject };
          }
          const { mapUserChannels } = notify;
          let foundForNotify = false;
          Object.keys(mapUserChannels).forEach(username => {
            const subItem = mapUserChannels[username];
            Object.keys(subItem).forEach(subKey => {
              if (parseInt(subItem[subKey], 10) > 0) {
                foundForNotify = true;
              }
            });
          });
          if (foundForNotify) {
            itemToPush['notify'] = { ...notify };
          }

          const { objectId } = setObjectState;
          if (objectId) {
            itemToPush['setObjectState'] = { ...setObjectState };
          }

          const { id: automationId } = runAutomation;
          if (automationId) {
            itemToPush['runAutomation'] = { ...runAutomation };
          }

          if (code) {
            itemToPush['code'] = code;
          }

          act.push(itemToPush);
        });
        return act;
      },
      handleCreateAutomation() {
        const events = [...this.storedEvents];
        const schedules = [...this.storedSchedules];
        const conditions = this.storedCondition.list.length > 0 ? [...this.storedCondition.list] : null;
        if (conditions) {
          delete conditions['visible'];
        }
        const actions = this.cleanActions([...this.storedActions]);
        this.createAutomation({
          name: this.name,
          events,
          schedules,
          conditions,
          actions
        });
        this.activeTab = 0;
        this.$router.push('/configure/automations');
      }
    },
    mounted() {
      if (this.userList && this.notificationHandlers) {
        this.userList.forEach(user => {
          this.action.notify.mapUserChannels[user.username] = {};
          this.notificationHandlers.forEach(handler => {
            this.action.notify.mapUserChannels[user.username][handler.channel] = 0;
          });
        });
      }

      this.currentScheduleId = 1;
      this.dateForYesterday.setDate(this.dateForYesterday.getDate() - 1);
    }
  }
</script>

<style scoped>

</style>